import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Bell, Calculator, Loader2, Mail, Share2, ShieldCheck, TrendingUp, Info, Images, Download } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import Seo from "../components/Seo";
import api from "../lib/api";
import { LMNP_2027 as CFG, simulateLmnp } from "../lib/lmnp2027";
import { shareLmnpResult } from "../lib/lmnpShare";
import { renderLmnpCarousel, downloadZip } from "../lib/lmnpCarousel";

const euro = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", ".")); return isNaN(n) ? 0 : n; };
const inp = "w-full px-3 py-2.5 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep transition-colors";
const lbl = "block text-xs font-semibold text-brand-green/70 mb-1.5";
const track = (event) => api.post("/simulateur-lmnp/track", { event }).catch(() => {});

const JSON_LD = {
  "@context": "https://schema.org", "@type": "WebApplication",
  name: "Simulateur LMNP 2027 — impact du projet de loi de finances",
  url: "https://www.360immoinvest.com/simulateur-lmnp-2027",
  applicationCategory: "FinanceApplication", operatingSystem: "Web", inLanguage: "fr-FR",
  offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
  publisher: { "@type": "Organization", name: "360immoinvest", url: "https://www.360immoinvest.com/" },
};

const PROFILES = {
  exempt: { cls: "bg-emerald-50 border-emerald-300 text-emerald-800", title: "Vous n'êtes pas concerné", txt: "Les résidences réservées aux étudiants, aux seniors et les Ehpad sont exclues du plafonnement dans le projet." },
  none: { cls: "bg-emerald-50 border-emerald-300 text-emerald-800", title: "Pas d'impact sur votre impôt", txt: "Avec vos chiffres, l'amortissement plafonné suffit à neutraliser votre résultat." },
  limited: { cls: "bg-amber-50 border-amber-300 text-amber-800", title: "Impact limité", txt: "Votre impôt augmenterait légèrement. Vérifiez vos hypothèses (valeur du bâti, charges)." },
  moderate: { cls: "bg-orange-50 border-orange-300 text-orange-800", title: "Impact notable", txt: "Le taux d'amortissement réduit à 2,5 % augmente sensiblement votre résultat imposable." },
  strong: { cls: "bg-red-50 border-red-300 text-red-800", title: "Impact fort : au-delà du plafond", txt: "Votre amortissement dépasse le plafond annuel par foyer : l'excédent serait définitivement perdu." },
};

const Field = ({ label, hint, children }) => (
  <div><label className={lbl}>{label}</label>{children}{hint && <p className="text-[11px] text-brand-green/50 mt-1">{hint}</p>}</div>
);

const Disclaimer = () => (
  <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="lmnp-disclaimer">
    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
    <p className="text-sm text-amber-900"><b>{CFG.status}</b> Estimation indicative, ne constitue pas un conseil fiscal. Dernière mise à jour : {new Date(CFG.updatedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.</p>
  </div>
);

export default function SimulateurLmnp2027() {
  const [f, setF] = useState({
    type: "classique", building: "200000", furniture: "10000", rents: "14000", charges: "3000",
    interests: "2000", tmi: 30, ps: String(CFG.psDefault), buildingRate: String(CFG.current.buildingRateDefault), stock: "",
    furnitureInCap: false,
  });
  const [shown, setShown] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); track("view"); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const res = useMemo(() => simulateLmnp({
    type: f.type, building: num(f.building), furniture: num(f.furniture), rents: num(f.rents), charges: num(f.charges),
    interests: num(f.interests), tmi: Number(f.tmi), ps: num(f.ps), buildingRate: num(f.buildingRate), stock: num(f.stock),
    furnitureInCap: f.furnitureInCap,
  }), [f]);

  const simulate = () => {
    if (num(f.building) <= 0 || num(f.rents) <= 0) { toast.error("Renseignez au moins la valeur du bâti et les loyers annuels."); return; }
    setShown(true); track("simulate");
    setTimeout(() => document.getElementById("lmnp-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="simulateur-lmnp-page">
      <Seo title="Simulateur LMNP 2027 : impact du budget sur votre impôt | 360immoinvest"
        description="Estimez en 1 minute l'impôt supplémentaire que pourrait vous coûter le plafonnement de l'amortissement LMNP prévu par le projet de loi de finances 2027. Gratuit, sans inscription."
        path="/simulateur-lmnp-2027" jsonLd={JSON_LD} />
      <Header />
      <main>
        <section className="pt-32 pb-8 bg-brand-ivoire">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <p className="text-brand-limeDark font-semibold mb-2">Outil gratuit · PLF 2027</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-greenDeep leading-tight">Simulateur LMNP 2027</h1>
            <p className="mt-4 text-base md:text-lg text-brand-green/70 max-w-2xl">En 1 minute, estimez l'impôt supplémentaire que pourrait vous coûter le plafonnement de l'amortissement en location meublée.</p>
            <div className="mt-6"><Disclaimer /></div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-5 lg:px-8 pb-16 grid lg:grid-cols-[1fr_1.05fr] gap-8">
          <LmnpForm f={f} set={set} setF={setF} onSimulate={simulate} />
          <div id="lmnp-result" className="lg:sticky lg:top-24 self-start">
            {shown ? <LmnpResult res={res} f={f} /> : <ResultPlaceholder />}
          </div>
        </div>

        <LmnpExplainer />
      </main>
      <Footer />
    </div>
  );
}

function LmnpForm({ f, set, setF, onSimulate }) {
  return (
    <div className="space-y-5" data-testid="lmnp-form">
      <fieldset className="rounded-2xl border border-brand-soft p-5 space-y-4">
        <legend className="px-2 font-semibold text-brand-greenDeep">Votre bien</legend>
        <Field label="Type de location">
          <select className={inp} value={f.type} onChange={set("type")} data-testid="lmnp-type">
            {CFG.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        {f.type === "services" && <p className="text-xs text-emerald-700 font-medium" data-testid="lmnp-exempt-hint">Ce type de résidence est exclu du plafonnement dans le projet.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Valeur du bâti, hors terrain (€)" hint="Environ 80 à 85 % du prix d'achat, selon la ville."><input inputMode="decimal" className={inp} value={f.building} onChange={set("building")} data-testid="lmnp-building" /></Field>
          <Field label="Valeur du mobilier (€)" hint="Facultatif"><input inputMode="decimal" className={inp} value={f.furniture} onChange={set("furniture")} data-testid="lmnp-furniture" /></Field>
        </div>
      </fieldset>

      <fieldset className="rounded-2xl border border-brand-soft p-5 space-y-4">
        <legend className="px-2 font-semibold text-brand-greenDeep">Revenus et charges annuels</legend>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Loyers annuels (€)"><input inputMode="decimal" className={inp} value={f.rents} onChange={set("rents")} data-testid="lmnp-rents" /></Field>
          <Field label="Charges annuelles (€)" hint="Taxe foncière, copro, assurance, gestion"><input inputMode="decimal" className={inp} value={f.charges} onChange={set("charges")} data-testid="lmnp-charges" /></Field>
          <Field label="Intérêts d'emprunt (€)"><input inputMode="decimal" className={inp} value={f.interests} onChange={set("interests")} data-testid="lmnp-interests" /></Field>
        </div>
      </fieldset>

      <fieldset className="rounded-2xl border border-brand-soft p-5 space-y-4">
        <legend className="px-2 font-semibold text-brand-greenDeep">Votre fiscalité</legend>
        <Field label="Tranche marginale d'imposition">
          <div className="flex flex-wrap gap-2" data-testid="lmnp-tmi">
            {CFG.tmiOptions.map((t) => (
              <button key={t} type="button" onClick={() => setF({ ...f, tmi: t })} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${Number(f.tmi) === t ? "bg-brand-greenDeep text-white border-brand-greenDeep" : "border-brand-soft text-brand-greenDeep hover:bg-brand-softer"}`} data-testid={`lmnp-tmi-${t}`}>{t} %</button>
            ))}
          </div>
        </Field>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Prélèvements sociaux (%)"><input inputMode="decimal" className={inp} value={f.ps} onChange={set("ps")} data-testid="lmnp-ps" /></Field>
          <Field label="Taux d'amortissement actuel du bâti (%)" hint="≈ 3,33 % (30 ans) par défaut"><input inputMode="decimal" className={inp} value={f.buildingRate} onChange={set("buildingRate")} data-testid="lmnp-building-rate" /></Field>
          <Field label="Amortissements en stock (€)" hint="Facultatif, non encore déduits"><input inputMode="decimal" className={inp} value={f.stock} onChange={set("stock")} data-testid="lmnp-stock" /></Field>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={f.furnitureInCap} onChange={set("furnitureInCap")} className="mt-1 accent-brand-greenDeep" data-testid="lmnp-furniture-cap" />
          <span className="text-sm text-brand-greenDeep">Hypothèse : mobilier inclus dans le plafond<span className="block text-xs text-brand-green/50">Point non tranché dans le projet. Par défaut, le mobilier reste hors plafond.</span></span>
        </label>
      </fieldset>

      <button onClick={onSimulate} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold transition-colors" data-testid="lmnp-simulate-btn">
        <Calculator className="w-5 h-5" /> Simuler mon impact
      </button>
    </div>
  );
}

const ResultPlaceholder = () => (
  <div className="rounded-3xl border border-dashed border-brand-soft bg-brand-softer/50 p-10 text-center" data-testid="lmnp-placeholder">
    <TrendingUp className="w-10 h-10 mx-auto text-brand-green/40" />
    <p className="mt-4 font-display text-2xl text-brand-greenDeep">Votre résultat s'affichera ici</p>
    <p className="mt-2 text-sm text-brand-green/60">Sans inscription. Le calcul se fait dans votre navigateur, aucune donnée n'est enregistrée.</p>
  </div>
);

function LmnpResult({ res, f }) {
  const p = PROFILES[res.profile];
  const rows = [
    ["Résultat imposable", res.taxableNow, res.taxableProj],
    ["Impôt (IR + prélèvements sociaux)", res.taxNow, res.taxProj],
  ];
  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="lmnp-result">
      <div className="rounded-3xl bg-brand-greenDeep text-white p-7">
        <p className="text-sm text-white/70">Impôt supplémentaire estimé dès {CFG.firstYear}</p>
        <p className="font-display text-5xl font-semibold mt-1" data-testid="lmnp-extra-year">{res.extraYear > 0.5 ? `+ ${euro.format(res.extraYear)}` : euro.format(0)}<span className="text-xl text-white/70"> / an</span></p>
        <p className="mt-3 text-sm text-white/80">Soit <b data-testid="lmnp-extra-10y">{euro.format(Math.max(0, res.extra10y))}</b> sur {CFG.horizon} ans ({CFG.firstYear}–{CFG.firstYear + CFG.horizon - 1}).</p>
      </div>
      <div className={`rounded-2xl border px-4 py-3 ${p.cls}`} data-testid={`lmnp-profile-${res.profile}`}>
        <p className="font-semibold text-sm">{p.title}</p><p className="text-xs mt-0.5">{p.txt}</p>
      </div>
      <div className="rounded-2xl border border-brand-soft overflow-hidden">
        <table className="w-full text-sm" data-testid="lmnp-detail-table">
          <thead className="bg-brand-softer text-brand-green/70 text-xs"><tr><th className="text-left p-3 font-semibold"></th><th className="text-right p-3 font-semibold">Aujourd'hui</th><th className="text-right p-3 font-semibold">Avec le projet</th></tr></thead>
          <tbody>
            {rows.map(([k, a, b]) => (
              <tr key={k} className="border-t border-brand-soft"><td className="p-3 text-brand-greenDeep">{k}</td><td className="p-3 text-right">{euro.format(a)}</td><td className="p-3 text-right font-semibold text-brand-greenDeep">{euro.format(b)}</td></tr>
            ))}
            <tr className="border-t border-brand-soft"><td className="p-3 text-brand-greenDeep">Amortissement perdu chaque année</td><td className="p-3 text-right">—</td><td className="p-3 text-right font-semibold text-red-700" data-testid="lmnp-lost">{euro.format(res.lostAmort)}</td></tr>
            {res.stockLost > 0.5 && <tr className="border-t border-brand-soft"><td className="p-3 text-brand-greenDeep">Stock d'amortissement perdu après {CFG.stock.lastYear}</td><td className="p-3 text-right">—</td><td className="p-3 text-right font-semibold text-red-700">{euro.format(res.stockLost)}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-brand-green/50">Plafond retenu : {res.rule.rate} % du bâti, {euro.format(res.rule.cap)} / an par foyer. Mobilier amorti sur {CFG.current.furnitureYears} ans, {f.furnitureInCap ? "inclus" : "hors"} plafond. Un seul bien pris en compte : le plafond étant par foyer, l'impact est plus fort si vous en détenez plusieurs.</p>
      <LmnpActions res={res} f={f} />
      <LmnpCarousel res={res} f={f} />
    </div>
  );
}

function LmnpCarousel({ res, f }) {
  const [imgs, setImgs] = useState(null);
  const [busy, setBusy] = useState(false);
  const data = {
    extraYear: res.extraYear, extra10y: res.extra10y, taxableNow: res.taxableNow, taxableProj: res.taxableProj, taxNow: res.taxNow, taxProj: res.taxProj,
    lostAmort: res.lostAmort, rate: res.rule.rate, cap: res.rule.cap, typeLabel: res.type.label, rents: num(f.rents), building: num(f.building), tmi: f.tmi,
    horizon: CFG.horizon, firstYear: CFG.firstYear,
  };
  const make = async () => {
    setBusy(true); track("carousel");
    try { setImgs(await renderLmnpCarousel(data)); } catch { toast.error("Création des visuels impossible"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-brand-soft p-4" data-testid="lmnp-carousel">
      <p className="font-semibold text-sm text-brand-greenDeep flex items-center gap-2"><Images className="w-4 h-4" /> Carrousel Instagram de ce résultat</p>
      <p className="text-xs text-brand-green/60 mt-1">6 visuels carrés 1080 px aux couleurs 360immoinvest, prêts à publier.</p>
      {!imgs ? (
        <button onClick={make} disabled={busy} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep text-white text-sm font-semibold disabled:opacity-60" data-testid="lmnp-carousel-make">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Créer les visuels</button>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="lmnp-carousel-preview">{imgs.map((u, i) => <img key={i} src={u} alt={`Visuel ${i + 1}/6`} className="w-full aspect-square rounded border border-brand-soft" data-testid={`lmnp-carousel-img-${i + 1}`} />)}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => downloadZip(imgs)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-lime text-brand-greenDeep text-sm font-semibold" data-testid="lmnp-carousel-download"><Download className="w-4 h-4" /> Télécharger les 6 images (ZIP)</button>
            <button onClick={make} className="px-4 py-2 rounded-full border border-brand-soft text-sm" data-testid="lmnp-carousel-refresh">Régénérer</button>
          </div>
        </>
      )}
    </div>
  );
}

function LmnpActions({ res, f }) {
  const [detailEmail, setDetailEmail] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState("");
  const payload = {
    extra_year: Math.round(res.extraYear), extra_10y: Math.round(res.extra10y), taxable_now: Math.round(res.taxableNow),
    taxable_project: Math.round(res.taxableProj), lost_amort: Math.round(res.lostAmort), tax_now: Math.round(res.taxNow),
    tax_project: Math.round(res.taxProj), type_label: res.type.label, tmi: f.tmi, ps: f.ps, furniture_in_cap: f.furnitureInCap,
  };
  const sendDetail = async () => {
    setBusy("detail");
    try { await api.post("/simulateur-lmnp/email-detail", { email: detailEmail, result: payload }); toast.success("Détail envoyé par e-mail"); setDetailEmail(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); } finally { setBusy(""); }
  };
  const sendAlert = async () => {
    if (!consent) { toast.error("Merci de cocher la case de consentement."); return; }
    setBusy("alert");
    try { await api.post("/simulateur-lmnp/alert", { email: alertEmail, consent }); toast.success("C'est noté : nous vous préviendrons dès le vote de la loi."); setAlertEmail(""); setConsent(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Inscription impossible"); } finally { setBusy(""); }
  };
  const share = async () => { track("share"); try { await shareLmnpResult(res.extraYear); } catch { toast.error("Partage impossible"); } };
  return (
    <div className="space-y-3" data-testid="lmnp-actions">
      <div className="rounded-2xl border border-brand-soft p-4">
        <p className="font-semibold text-sm text-brand-greenDeep flex items-center gap-2"><Mail className="w-4 h-4" /> Recevoir le détail par e-mail</p>
        <div className="mt-2 flex gap-2">
          <input type="email" placeholder="votre@email.fr" value={detailEmail} onChange={(e) => setDetailEmail(e.target.value)} className={inp} data-testid="lmnp-detail-email" />
          <button onClick={sendDetail} disabled={busy === "detail" || !detailEmail} className="shrink-0 px-4 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60 transition-colors" data-testid="lmnp-detail-send">{busy === "detail" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}</button>
        </div>
      </div>
      <div className="rounded-2xl border border-brand-soft p-4">
        <p className="font-semibold text-sm text-brand-greenDeep flex items-center gap-2"><Bell className="w-4 h-4" /> Recevez une alerte quand la loi sera votée</p>
        <div className="mt-2 flex gap-2">
          <input type="email" placeholder="votre@email.fr" value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} className={inp} data-testid="lmnp-alert-email" />
          <button onClick={sendAlert} disabled={busy === "alert" || !alertEmail} className="shrink-0 px-4 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold disabled:opacity-60 transition-colors" data-testid="lmnp-alert-send">{busy === "alert" ? <Loader2 className="w-4 h-4 animate-spin" /> : "M'alerter"}</button>
        </div>
        <label className="mt-2 flex items-start gap-2 text-xs text-brand-green/70 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-brand-greenDeep" data-testid="lmnp-alert-consent" />
          J'accepte de recevoir des e-mails de 360immoinvest (alerte de vote et newsletter). Désinscription en un clic.
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/connexion" onClick={() => track("signup")} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold transition-colors" data-testid="lmnp-signup-cta">Suivre mes amortissements <ArrowUpRight className="w-4 h-4" /></Link>
        <button onClick={share} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold transition-colors" data-testid="lmnp-share-btn"><Share2 className="w-4 h-4" /> Partager mon résultat</button>
      </div>
      <p className="text-[11px] text-brand-green/50 text-center">Suivez vos amortissements bien par bien dans 360immoinvest : compte gratuit pour 1 bien.</p>
    </div>
  );
}

const LmnpExplainer = () => (
  <section className="bg-brand-softer py-14">
    <div className="max-w-3xl mx-auto px-5 lg:px-8 space-y-5 text-sm text-brand-green/75 leading-relaxed">
      <h2 className="font-display text-2xl font-semibold text-brand-greenDeep flex items-center gap-2"><ShieldCheck className="w-6 h-6" /> Ce que prévoit le projet</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li>Amortissement des locaux limité à <b>2,5 % par an</b> et <b>7 000 € par an et par foyer fiscal</b> (1,5 % et 5 000 € en meublé de tourisme).</li>
        <li>Fin du report illimité : l'amortissement non utilisé serait perdu.</li>
        <li>Stock existant au 31/12/2026 déductible de 2027 à 2036, dans la limite de 50 % du bénéfice restant.</li>
        <li>Exceptions : résidences étudiants, jeunes en formation, plus de 65 ans, Ehpad et certains établissements de soins.</li>
      </ul>
      <div className="flex items-start gap-3"><Info className="w-5 h-5 text-brand-greenDeep shrink-0 mt-0.5" /><p>Les taux et plafonds seront mis à jour dès l'adoption du texte. Pour une décision d'investissement, faites valider votre situation par un expert-comptable.</p></div>
    </div>
  </section>
);
